import React, { useEffect } from 'react';
import SiteHeader from '../../shared/SiteHeader';
import { Link } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import './About.css';

const About: React.FC = () => {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    document.title = 'Walk It With Me - About';
  }, []);

  return (
    <>
      <SiteHeader />
      <main className="container py-5">
        <h1 className="h2 mb-4">About Walk It With Me</h1>
        <p className="lead">
          Walk It With Me uses AI to make thoughtful, contextual Bible exploration accessible —
          helping you understand Scripture in its original setting and see how its wisdom speaks
          into your everyday life.
        </p>
        <section className="mb-4">
          <h2 className="h4">Vision</h2>
          <p>
            To make thoughtful, contextual Bible exploration accessible to everyone — helping people
            understand Scripture in its original setting, connect it to the real challenges of daily
            life, and grow in a faith that feels personal, grounded, and deeply relevant. Walk It
            With Me exists to bridge the gap between Biblical wisdom and modern questions, offering
            guidance that is Scripture-based, clear, compassionate, and shaped around each person’s
            unique journey.
          </p>
        </section>
        <section className="mb-4">
          <h2 className="h4 mb-2">See It In Action</h2>
          <p className="text-muted mb-4">
            No account needed — these are real AI conversations. Explore and see for yourself.
          </p>

          <h3 className="h6 mb-3">Ask Any Kind of Question</h3>
          <p className="mb-4">
            You can ask Walk It With Me any kind of question about the Bible, and it will provide
            thoughtful, contextual responses based on Scripture.
          </p>
          <div className="row g-3 mb-2">
            <div className="col-md-4">
              <div className="sia-topic-card">
                <div className="sia-topic-label">Interpretation &amp; Context</div>
                <p className="sia-topic-question">
                  &ldquo;What does Jesus mean when He says &lsquo;My yoke is easy and my burden is
                  light&rsquo;?&rdquo;
                </p>
                <Link to="/sample/chat/yoke-and-burden" className="sia-read-link stretched-link">
                  Read this conversation
                </Link>
              </div>
            </div>
            <div className="col-md-4">
              <div className="sia-topic-card">
                <div className="sia-topic-label">Practical Guidance</div>
                <p className="sia-topic-question">
                  &ldquo;I&rsquo;m struggling with patience at work. What verses can help me stay
                  calm and respond with grace?&rdquo;
                </p>
                <Link
                  to="/sample/chat/prayer-for-patience"
                  className="sia-read-link stretched-link"
                >
                  Read this conversation
                </Link>
              </div>
            </div>
            <div className="col-md-4">
              <div className="sia-topic-card">
                <div className="sia-topic-label">Teaching Mode</div>
                <p className="sia-topic-question">
                  &ldquo;Can you explain the main theme of the Book of James?&rdquo;
                </p>
                <Link to="/sample/chat/theme-of-james" className="sia-read-link stretched-link">
                  Read this conversation
                </Link>
              </div>
            </div>
          </div>

          <div className="sia-persona-block">
            <h3 className="h6 mb-1">Answers Tailored to You</h3>
            <p className="text-muted small mb-0">
              One question. Four completely different answers — because your profile shapes every
              response.
            </p>
            <div className="sia-shared-question-callout">
              <div className="sia-question-icon" aria-hidden="true">
                <i className="bi bi-person-fill" />
              </div>
              <p className="sia-shared-question-text">
                &ldquo;What does it mean that God is always with me?&rdquo;
              </p>
            </div>
            <div className="row g-3">
              <div className="col-6 col-lg-3">
                <div className="sia-persona-card">
                  <div className="sia-persona-name">Child</div>
                  <div className="sia-persona-descriptor">Simple language, relatable examples</div>
                  <p className="sia-persona-excerpt">
                    The Bible tells us many times that God is always with us, no matter where we
                    are. We&apos;re never alone — God cares about us like a loving friend or parent.
                  </p>
                  <Link to="/sample/chat/child" className="sia-persona-read stretched-link">
                    Read this answer
                  </Link>
                </div>
              </div>
              <div className="col-6 col-lg-3">
                <div className="sia-persona-card">
                  <div className="sia-persona-name">Teen</div>
                  <div className="sia-persona-descriptor">Conversational, honest, real</div>
                  <p className="sia-persona-excerpt">
                    Believing that God is always with you is a central theme in the Bible, offering
                    comfort, guidance, and reassurance — especially in times of uncertainty or
                    challenge.
                  </p>
                  <Link to="/sample/chat/teen" className="sia-persona-read stretched-link">
                    Read this answer
                  </Link>
                </div>
              </div>
              <div className="col-6 col-lg-3">
                <div className="sia-persona-card">
                  <div className="sia-persona-name">Adult</div>
                  <div className="sia-persona-descriptor">
                    Deeper context, practical application
                  </div>
                  <p className="sia-persona-excerpt">
                    This is a foundational promise in the Bible, offering deep comfort and strength,
                    especially in busy or challenging seasons of life.
                  </p>
                  <Link to="/sample/chat/adult" className="sia-persona-read stretched-link">
                    Read this answer
                  </Link>
                </div>
              </div>
              <div className="col-6 col-lg-3">
                <div className="sia-persona-card">
                  <div className="sia-persona-name">Senior</div>
                  <div className="sia-persona-descriptor">Reflective, wisdom-focused</div>
                  <p className="sia-persona-excerpt">
                    This is a profound question — and a comforting truth. No matter our
                    circumstances — joys, sorrows, routines, or struggles — God&apos;s presence,
                    love, and care are constant.
                  </p>
                  <Link to="/sample/chat/senior" className="sia-persona-read stretched-link">
                    Read this answer
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="dev-persona-block">
            <h3 className="h6 mb-1">A Devotional Written for Your Season of Life</h3>
            <p className="text-muted small mb-0">
              Every morning, a full devotional shaped around your profile — your age, your faith
              journey, and the themes on your heart.
            </p>
            <div className="dev-scripture-callout">
              <div className="dev-scripture-eyebrow">
                Today&rsquo;s Verse &middot; Psalm 139:23&ndash;24
              </div>
              <p className="dev-scripture-text">
                &ldquo;Search me, God, and know my heart. Try me, and know my thoughts.&rdquo;
              </p>
            </div>
            <div className="row g-3">
              <div className="col-6 col-lg-3">
                <div className="dev-persona-card">
                  <div className="dev-persona-name">Child</div>
                  <div className="dev-persona-descriptor">Simple, warm, wonder-filled</div>
                  <p className="dev-persona-excerpt">
                    God knows us better than anyone. Just like a superhero has a helper, God is
                    always ready to guide you and help you make good choices.
                  </p>
                  <Link to="/sample/devotional/child" className="dev-persona-read stretched-link">
                    Read this devotional
                  </Link>
                </div>
              </div>
              <div className="col-6 col-lg-3">
                <div className="dev-persona-card">
                  <div className="dev-persona-name">Teen</div>
                  <div className="dev-persona-descriptor">Honest, real, relatable</div>
                  <p className="dev-persona-excerpt">
                    As a teen juggling music, sports, social media, and life&rsquo;s pressures,
                    it&rsquo;s easy to feel overwhelmed. David&rsquo;s prayer says it&rsquo;s okay
                    to ask God to search your heart.
                  </p>
                  <Link to="/sample/devotional/teen" className="dev-persona-read stretched-link">
                    Read this devotional
                  </Link>
                </div>
              </div>
              <div className="col-6 col-lg-3">
                <div className="dev-persona-card">
                  <div className="dev-persona-name">Adult</div>
                  <div className="dev-persona-descriptor">Depth, context, application</div>
                  <p className="dev-persona-excerpt">
                    This passage reminds us of God&rsquo;s deep knowledge of us — He sees not only
                    our actions, but our feelings and motivations.
                  </p>
                  <Link to="/sample/devotional/adult" className="dev-persona-read stretched-link">
                    Read this devotional
                  </Link>
                </div>
              </div>
              <div className="col-6 col-lg-3">
                <div className="dev-persona-card">
                  <div className="dev-persona-name">Senior</div>
                  <div className="dev-persona-descriptor">Reflective, wisdom-rich, peaceful</div>
                  <p className="dev-persona-excerpt">
                    David invites God to lead him &ldquo;in the way everlasting&rdquo; — a gentle
                    promise of renewal, releasing burdens we&rsquo;ve carried too long.
                  </p>
                  <Link to="/sample/devotional/senior" className="dev-persona-read stretched-link">
                    Read this devotional
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="mb-4">
          <h2 className="h5">Privacy & Safety</h2>
          <p>
            Your data is protected using industry-standard security practices. Future updates will
            include more granular privacy controls. See our{' '}
            <Link to="/privacy">Privacy Policy</Link> for full details.
          </p>
        </section>
        <p className="text-muted">
          Beta version – features and experience will evolve rapidly. Thank you for trying Walk It
          With Me!
        </p>
        {!isAuthenticated && (
          <div className="mt-3">
            <Link to="/login#signup" className="btn btn-primary btn-lg">
              Sign Up
            </Link>
          </div>
        )}
      </main>
    </>
  );
};

export default About;
